// public/js/services/twilioVoiceBootstrap.js
/**
 * @module  public/js/services/twilioVoiceBootstrap.js
 * @summary Shared Twilio Voice bootstrapper used by Focus Mode & Outreach.
 *
 * @description
 *  Provides a single entry-point (`ensureTwilioReady`) that
 *  – verifies the `twilioVoice` feature flag,
 *  – fetches/refreshes access tokens,
 *  – primes the Twilio Device via `twilio-voice-service`, and
 *  – performs a microphone permission pre-flight.
 *
 *  Callers receive a memoised promise on the DI container (`di.twilioReady`)
 *  so repeated invocations share the same async work.  Failures reset the
 *  memoised promise allowing later retries.
 */

import { DEFAULT_TWILIO_EDGE } from './twilioEdgeConfiguration.js';
import useTenant from '../useTenant.js';

let twilioVoiceServicePromise = null;

/**
 * Resolve the Twilio Voice service only when browser calling needs it.
 *
 * @returns {Promise<import('./twilio-voice-service.js').default>} Twilio Voice service singleton.
 */
export async function resolveTwilioVoiceService() {
  if (!twilioVoiceServicePromise) {
    twilioVoiceServicePromise = import('./twilio-voice-service.js').then(
      (module) => module.default
    );
  }
  return twilioVoiceServicePromise;
}

/**
 * Determine the tenant identifier used for token requests.
 *
 * @param {Record<string, any>} di - The shared dependency container.
 * @returns {string} tenant identifier string.
 */
function resolveTenantId(di = {}) {
  return (
    di.tenant?.getTenantId?.() ||
    di.auth?.currentUser?.tenantId ||
    useTenant(di.auth ?? {})
  );
}

/**
 * Fetch a fresh Twilio Voice token from the backend.
 *
 * @param {Record<string, any>} di - Dependency container providing endpoints.
 * @param {string} tenantId - Tenant identifier for the request.
 * @returns {Promise<string>} JWT token string.
 */
export async function fetchVoiceToken(di = {}, tenantId = '') {
  if (!di.endpoints?.voiceToken) {
    throw new Error('Voice token endpoint unavailable.');
  }
  const url = di.endpoints.voiceToken({ tenantId });
  const headers = new Headers();

  const authSvc = di.auth || window?.di?.auth;
  if (!authSvc) {
    throw new Error('Auth service unavailable for voice token fetch.');
  }

  let user = null;
  if (typeof authSvc.requireAuth === 'function') {
    user = await authSvc.requireAuth();
  } else if (typeof authSvc.waitForLogin === 'function') {
    user = await authSvc.waitForLogin();
    if (!user) {
      throw new Error('Voice token fetch requires authenticated user.');
    }
  } else if (authSvc.currentUser) {
    user = authSvc.currentUser;
  }

  if (!user?.getIdToken) {
    throw new Error('Unable to obtain ID token for voice token request.');
  }

  const idToken = await user.getIdToken();
  headers.set('Authorization', `Bearer ${idToken}`);
  if (tenantId) {
    headers.set('x-tenant-id', tenantId);
  }

  const res = await fetch(url, { headers });
  if (!res.ok) {
    throw new Error('Failed to fetch Twilio access token.');
  }
  const payload = await res.json();
  console.log('[twilioVoiceBootstrap.fetchVoiceToken.payload]', payload);
  const token =
    typeof payload === 'string'
      ? payload
      : typeof payload?.token === 'string'
        ? payload.token
        : '';
  if (!token) {
    throw new Error('Token fetch failed');
  }
  const twilioVoice = await resolveTwilioVoiceService();
  if (typeof twilioVoice?.setSessionMeta === 'function') {
    const sessionMeta = {
      uid: payload?.uid || user?.uid || null,
      tenantId: payload?.tenantId || tenantId || null,
      from: payload?.maybeFrom || payload?.callerId || null,
      correlationId: payload?.correlationId || null,
    };
    twilioVoice.setSessionMeta(sessionMeta);
    console.log(
      '[twilioVoiceBootstrap.fetchVoiceToken.sessionMeta]',
      sessionMeta
    );
  }
  return token;
}

/**
 * Ensure the microphone is available and not explicitly blocked.
 *
 * @returns {Promise<void>} resolves when access is confirmed.
 */
async function ensureMicrophoneAccess() {
  try {
    const perm = await navigator.permissions?.query?.({ name: 'microphone' });
    if (perm && perm.state === 'denied') {
      throw new Error(
        'Microphone access is blocked. Allow access in your browser settings to place calls.'
      );
    }
  } catch {
    /* Permissions API unsupported – continue with best effort */
  }

  const hasGUM =
    typeof navigator !== 'undefined' &&
    !!navigator.mediaDevices &&
    typeof navigator.mediaDevices.getUserMedia === 'function';
  if (!hasGUM) {
    throw new Error('This browser does not support microphone access.');
  }

  const stream = await navigator.mediaDevices.getUserMedia({
    audio: { echoCancellation: true },
  });
  stream.getTracks().forEach((track) => track.stop());
}

/**
 * Resolve whether browser-based calling is permitted for the current tenant.
 *
 * @param {Record<string, any>} di - Dependency container exposing featureFlag.
 * @returns {Promise<void>} resolves when calling is confirmed.
 */
async function ensureCallingEnabled(di = {}) {
  try {
    const enabled = await di.featureFlag?.isEnabled?.('twilioVoice');
    const allow = enabled !== false;
    if (typeof di.canCall !== 'undefined') {
      try {
        di.canCall = allow;
      } catch {
        /* Some Focus Mode DI objects expose canCall as a computed getter. */
      }
    }
    if (!allow) {
      throw new Error('Browser calling disabled for this tenant.');
    }
  } catch (err) {
    // If the feature check fails, allow calling (parity with Focus Mode).
    try {
      di.canCall = true;
    } catch {
      /* Some Focus Mode DI objects expose canCall as a computed getter. */
    }
    if (err?.message === 'Browser calling disabled for this tenant.') {
      throw err;
    }
  }
}

/**
 * Ensure the Twilio Voice client device is initialized and registered without
 * requesting microphone access.
 *
 * @param {Record<string, any>} di - Shared dependency container.
 * @param {{
 *   force?: boolean,
 *   edge?: string | string[],
 *   deviceOptions?: import('@twilio/voice-sdk').Device.Options
 * }} [options] - Optional configuration.
 * @returns {Promise<boolean>} resolves once device initialisation completes.
 */
export async function ensureTwilioDeviceReady(di = {}, options = {}) {
  if (!di) throw new Error('Missing dependency container');
  const {
    force = false,
    deviceOptions: providedDeviceOptions = {},
    edge,
  } = options;

  if (!force && di.twilioDeviceReady) {
    return di.twilioDeviceReady;
  }

  const run = (async () => {
    await ensureCallingEnabled(di);
    const tenantId = resolveTenantId(di);
    const tokenRefresher = async () => fetchVoiceToken(di, tenantId);
    const configuredEdge =
      edge ??
      providedDeviceOptions.edge ??
      di.twilioEdge ??
      DEFAULT_TWILIO_EDGE;

    const deviceOptions = {
      ...providedDeviceOptions,
      edge: configuredEdge,
    };

    const twilioVoice = await resolveTwilioVoiceService();
    await twilioVoice.prewarm({
      tokenProvider: tokenRefresher,
      options: deviceOptions,
    });
    return true;
  })();

  di.twilioDeviceReady = run.catch((err) => {
    di.twilioDeviceReady = null;
    throw err;
  });

  await di.twilioDeviceReady;
  return true;
}

/**
 * Ensure the Twilio Voice client is ready for outbound calls, including the
 * microphone preflight required before an actual dial.
 *
 * @param {Record<string, any>} di - Shared dependency container.
 * @param {{
 *   force?: boolean,
 *   edge?: string | string[],
 *   deviceOptions?: import('@twilio/voice-sdk').Device.Options
 * }} [options] - Optional configuration.
 * @returns {Promise<boolean>} resolves once initialisation completes.
 */
export async function ensureTwilioReady(di = {}, options = {}) {
  if (!di) throw new Error('Missing dependency container');
  const { force = false } = options;

  if (!force && di.twilioReady) {
    return di.twilioReady;
  }

  const run = (async () => {
    await ensureTwilioDeviceReady(di, options);
    await ensureMicrophoneAccess();
    return true;
  })();

  di.twilioReady = run.catch((err) => {
    di.twilioReady = null;
    throw err;
  });

  await di.twilioReady;
  return true;
}

export default ensureTwilioReady;
